/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';

interface ChartContainerProps {
  /** Chart title */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Chart content */
  children: React.ReactNode;
  /** Container height (default: 300px) */
  height?: number | string;
  /** Action buttons slot */
  actions?: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  empty?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Export handler */
  onExport?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Use gradient title */
  gradientTitle?: boolean;
  /** Chart element ID for export */
  id?: string;
}

/**
 * ChartContainer - Reusable wrapper for all charts
 * Provides consistent styling, loading states, and export functionality
 */
const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  subtitle,
  children,
  height = 300,
  actions,
  loading = false,
  empty = false,
  emptyMessage = 'No data available',
  onExport,
  className = '',
  gradientTitle = false,
  id,
}) => {
  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      id={id}
      className={`chart-container rounded-xl p-6 transition-all duration-300 ${className}`}
    >
      {/* Header */}
      {(title || actions || onExport) && (
        <div className="flex items-start justify-between mb-4">
          <div>
            {title && (
              <h3
                className={`text-lg font-semibold ${
                  gradientTitle ? 'text-gradient' : 'text-foreground'
                }`}
              >
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-text-muted mt-1">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            {onExport && (
              <button
                onClick={onExport}
                className="btn btn-ghost p-2"
                title="Export chart"
              >
                <i className="fas fa-download text-text-muted" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ height: heightStyle }} className="relative">
        {loading ? (
          <LoadingSkeleton />
        ) : empty ? (
          <EmptyState message={emptyMessage} />
        ) : (
          children
        )}
      </div>
    </div>
  );
};

/**
 * Loading skeleton for chart
 */
const LoadingSkeleton: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="w-full h-full flex flex-col items-center justify-center gap-4">
      <div className="w-3/4 h-4 bg-background-tertiary rounded animate-shimmer" />
      <div className="w-1/2 h-4 bg-background-tertiary rounded animate-shimmer" />
      <div className="w-full h-40 bg-background-tertiary rounded animate-shimmer" />
      <div className="flex gap-4 w-full">
        <div className="flex-1 h-3 bg-background-tertiary rounded animate-shimmer" />
        <div className="flex-1 h-3 bg-background-tertiary rounded animate-shimmer" />
        <div className="flex-1 h-3 bg-background-tertiary rounded animate-shimmer" />
      </div>
    </div>
  </div>
);

/**
 * Empty state for chart
 */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="empty-state">
      <div className="empty-icon">
        <i className="fas fa-chart-bar text-text-muted text-2xl" />
      </div>
      <p className="empty-title">No Data</p>
      <p className="empty-description">{message}</p>
    </div>
  </div>
);

export default ChartContainer;
